/**
 * app/navigation/UnifiedNavigator.tsx
 * -----------------------------------
 * What: Top-level navigation composition. Renders auth stack when signed out and
 * the main app stack with a custom tab bar (sidebar on desktop) when signed in.
 * Why: Centralises routing, title updates, deep-link handling, and platform-
 * specific layout differences (web vs native, desktop vs mobile).
 */
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { View, TouchableOpacity, Text, StyleSheet, useWindowDimensions, Animated, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NavigationContainer, useNavigation, useNavigationState, useNavigationContainerRef } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useNavigationContext } from './NavigationContext';
import { useAuth } from '../../lib/AuthContext';
import type { RootStackParamList } from '../types/navigation';
import { TITLES } from '../components/HeaderBar';
import { Platform as RNPlatform } from 'react-native';
import { useTheme } from '../../lib/ThemeContext';
import { useSubscription } from '../../lib/SubscriptionContext';
import { useData } from '../../lib/DataContext';

// Screens
import LoginScreen from '../screens/LoginScreen';
import HomeScreen from '../screens/HomeScreen';
import RoomListScreen from '../screens/RoomList';
import ItemListScreen from '../screens/ItemList';
import SettingsScreen from '../screens/SettingsScreen';
import AddRoomScreen from '../screens/AddRoomScreen';
import EditRoomScreen from '../screens/EditRoomScreen';
import AddItemScreen from '../screens/AddItemScreen';
import EditItemScreen from '../screens/EditItemScreen';
import ViewItemScreen from '../screens/ViewItemScreen';
import HeaderBar from '../components/HeaderBar';
import SignUpScreen from '../screens/SignUpScreen';

// Use native-stack on web to avoid RNW shadow* warnings from @react-navigation/stack's Card styles
const createStack: any = Platform.OS === 'web'
  ? require('@react-navigation/native-stack').createNativeStackNavigator
  : require('@react-navigation/stack').createStackNavigator;
const Stack = createStack();
const Tab = createBottomTabNavigator<RootStackParamList>();

type NavigationProp = StackNavigationProp<RootStackParamList>;

const NAV_ITEMS = [
  { key: 'HomeTab' as keyof RootStackParamList, icon: 'home', label: 'Home', component: HomeScreen },
  { key: 'RoomsTab' as keyof RootStackParamList, icon: 'grid', label: 'Rooms', component: RoomListScreen },
  { key: 'ItemsTab' as keyof RootStackParamList, icon: 'list', label: 'Items', component: ItemListScreen },
  { key: 'SettingsTab' as keyof RootStackParamList, icon: 'settings', label: 'Settings', component: SettingsScreen },
] as const;

function isNamedRoute(route: any): route is { name: string; key: string } {
  return (
    route &&
    typeof route.name === 'string' &&
    typeof route.key === 'string'
  );
}

function getTabForRoute(routeName: string): keyof RootStackParamList | undefined {
  if (["AddRoom", "EditRoom"].includes(routeName)) return "RoomsTab";
  if (["AddItem", "EditItem", "ViewItem"].includes(routeName)) return "ItemsTab";
  if (["SettingsTab"].includes(routeName)) return "SettingsTab";
  if (["HomeTab"].includes(routeName)) return "HomeTab";
  return routeName as keyof RootStackParamList;
}

function MainContent() {
  const { isDesktop, setActiveTab, activeTab } = useNavigationContext();
  const [expanded, setExpanded] = React.useState(false);
  const navigation = useNavigation<NavigationProp>();
  const navState = useNavigationState((state) => state);
  const { theme, themeType } = useTheme();

  const buildIconGlow = () => {
    if (themeType !== 'blueNeon') return {};
    return Platform.OS === 'web'
      ? { filter: `drop-shadow(0 0 4px ${theme.accent})` } as any
      : { shadowColor: theme.accent, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.7, shadowRadius: 4, elevation: 4 };
  };

  const hasNavigated = useRef(false);
  const [isNavigatorReady, setIsNavigatorReady] = React.useState(false);
  
  console.log('🧭 [UnifiedNavigator] MainContent rendered with state:', {
    isDesktop,
    activeTab,
    expanded,
    hasNavigated: hasNavigated.current,
    isNavigatorReady,
    navStateIndex: navState?.index,
    navStateRoutes: navState?.routes?.length
  });
  
  // Sidebar width animation
  const sidebarWidth = expanded ? 180 : 64;

  // Get the current route name for tab highlighting
  let currentRouteName: keyof RootStackParamList = 'HomeTab';
  if (navState) {
    let current = navState.routes[navState.index];
    while (current && 'state' in current && current.state && (current.state as any).index !== undefined) {
      current = (current.state as any).routes[(current.state as any).index];
    }
    if (current && typeof current.name === 'string') {
      currentRouteName = getTabForRoute(current.name) as keyof RootStackParamList;
    }
  }

  console.log('🧭 [UnifiedNavigator] Current route analysis:', {
    currentRouteName,
    navStateIndex: navState?.index,
    navStateRouteName: navState?.routes?.[navState?.index]?.name
  });

  // Sync activeTab in context with the parent tab on mount and nav state changes
  useEffect(() => {
    console.log('🔄 [UnifiedNavigator] Syncing activeTab with currentRouteName:', currentRouteName);
    if (currentRouteName) {
      setActiveTab(currentRouteName);
    } else {
      setActiveTab('HomeTab');
    }
  }, [currentRouteName, setActiveTab]);

  const handleTabPress = (tabKey: keyof RootStackParamList) => {
    console.log('👆 [UnifiedNavigator] Tab pressed:', tabKey, 'current activeTab:', activeTab);
    setActiveTab(tabKey);
    console.log('🧭 [UnifiedNavigator] Navigating to tab:', tabKey);
    navigation.navigate(tabKey as any);
  };

  // On mount, navigate to the restored activeTab if needed
  useEffect(() => {
    console.log('🚀 [UnifiedNavigator] Mount effect, hasNavigated:', hasNavigated.current, 'navigation ready:', !!navigation);
    if (!hasNavigated.current && navigation) {
      hasNavigated.current = true;
      if (!currentRouteName || currentRouteName === 'HomeTab') {
        console.log('🏠 [UnifiedNavigator] Setting default tab to HomeTab');
        setActiveTab('HomeTab');
      } else {
        console.log('🧭 [UnifiedNavigator] Navigating to restored route:', currentRouteName);
        navigation.navigate(currentRouteName as any);
      }
    }
  }, [currentRouteName, navigation, setActiveTab]);

  const renderTabBar = () => {
    if (isDesktop) {
      return (
        <Animated.View style={[
          styles.sidebar,
          { 
            width: sidebarWidth, 
            backgroundColor: theme.card, 
            borderRightColor: theme.border 
          }
        ]}>
          <TouchableOpacity
            style={[
              styles.toggleButton,
              expanded ? { alignSelf: 'flex-end', marginRight: 8 } : { alignSelf: 'center', marginRight: 0 }
            ]}
            onPress={() => setExpanded((prev) => !prev)}
          >
            <Ionicons name={expanded ? 'chevron-back' : 'chevron-forward'} size={24} color={theme.text} />
          </TouchableOpacity>
          {NAV_ITEMS.map((item) => (
            <TouchableOpacity
              key={item.key}
              style={[
                styles.navItem,
                expanded ? null : { width: 'auto', paddingHorizontal: 0, justifyContent: 'center' }
              ]}
              onPress={() => handleTabPress(item.key)}
            >
              <Ionicons
                name={item.icon as any}
                size={24}
                color={activeTab === item.key ? theme.accent : theme.secondaryText}
                style={activeTab === item.key ? buildIconGlow() : undefined}
              />
              {expanded && (
                <Text
                  style={[
                    styles.navLabel,
                    { color: activeTab === item.key ? theme.accent : theme.text },
                    activeTab === item.key && { fontWeight: 'bold' }
                  ]}
                >
                  {item.label}
                </Text>
              )}
            </TouchableOpacity>
          ))}
        </Animated.View>
      );
    }

    // Mobile web: use a fixed <div>
    if (Platform.OS === 'web' && !isDesktop) {
      return (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            width: '100vw',
            background: theme.card,
            borderTop: `1px solid ${theme.border}`,
            display: 'flex',
            flexDirection: 'row',
            paddingBottom: 12,
            paddingTop: 12,
          }}
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
                border: 'none',
                outline: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                cursor: 'pointer',
                color: activeTab === item.key ? theme.accent : theme.text,
              }}
              onClick={() => handleTabPress(item.key)}
            >
              <Ionicons
                name={item.icon as any}
                size={24}
                color={activeTab === item.key ? theme.accent : theme.secondaryText}
                style={activeTab === item.key ? buildIconGlow() : undefined}
              />
              <span
                style={{
                  color: activeTab === item.key ? theme.accent : theme.text,
                  fontWeight: activeTab === item.key ? 'bold' : 'normal',
                  fontSize: 11,
                  letterSpacing: 0.2,
                  marginTop: 2,
                }}
              >
                {item.label}
              </span>
            </button>
          ))}
        </div>
      );
    }

    // Native mobile: use View
    return (
      <View style={[styles.tabBar, { backgroundColor: theme.card, borderTopColor: theme.border }]}> 
        {NAV_ITEMS.map((item) => (
          <TouchableOpacity
            key={item.key}
            style={styles.tabItem}
            onPress={() => handleTabPress(item.key)}
          >
            <Ionicons name={item.icon as any} size={24} color={activeTab === item.key ? theme.accent : theme.secondaryText} />
            <Text
              style={[
                styles.tabLabel,
                { color: activeTab === item.key ? theme.accent : theme.text },
                activeTab === item.key && { fontWeight: 'bold' }
              ]}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  useEffect(() => {
    if (RNPlatform.OS === 'web' && navState) {
      // Find the current route (deepest route in the stack)
      let current = navState.routes[navState.index];
      while (current && 'state' in current && current.state && current.state.index !== undefined) {
        const next = current.state.routes[current.state.index];
        if (!isNamedRoute(next)) break;
        current = next;
      }
      if (!isNamedRoute(current)) return;
      const routeName = current.name;
      let section = '';
      if (routeName === 'HomeTab') {
        document.title = 'Log My Home';
        return;
      } else if (TITLES[routeName]) {
        section = TITLES[routeName];
      } else if (routeName === 'AddRoom') {
        section = 'Add Room';
      } else if (routeName === 'EditRoom') {
        section = 'Edit Room';
      } else if (routeName === 'AddItem') {
        section = 'Add Item';
      } else if (routeName === 'EditItem') {
        section = 'Edit Item';
      } else if (routeName === 'ViewItem') {
        section = 'View Item';
      }
      document.title = section ? `Log My Home - ${section}` : 'Log My Home';
    }
  }, [navState]);

  useEffect(() => {
    if (navState) {
      setIsNavigatorReady(true);
    }
  }, [navState]);

  // For web, wrap in a div with safe area env padding and background
  if (RNPlatform.OS === 'web') {
    // Memoize the style object to prevent remounts
    const webRootStyle = React.useMemo(() => ({
      display: 'flex',
      flexDirection: 'row' as const,
      paddingTop: 'env(safe-area-inset-top)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      background: theme.background,
      minHeight: '100vh',
      height: '100vh',
      boxSizing: 'border-box' as const,
      width: '100vw',
    }), [theme.background]);
    return (
      <div style={webRootStyle}>
        <View style={[styles.root, !isDesktop && styles.mobileRoot, { backgroundColor: theme.background }]}> 
          {isDesktop && renderTabBar()}
          <View style={[
            styles.content, 
            { backgroundColor: theme.background },
            Platform.OS === 'web' && !isDesktop && { paddingBottom: 80 }
          ]}> 
            <HeaderBar titleKey={currentRouteName as keyof RootStackParamList} />
            <Stack.Navigator 
              screenOptions={{ 
                headerShown: false,
                presentation: 'card',
                headerStyle: { backgroundColor: theme.card, borderBottomColor: theme.border },
                headerTintColor: theme.text,
              // Avoid deprecated shadow* props on web by disabling card shadows
              ...(Platform.OS === 'web' ? { cardShadowEnabled: false } : {}),
              }}
              initialRouteName={currentRouteName as keyof RootStackParamList}
            >
              {NAV_ITEMS.map((item) => (
                <Stack.Screen
                  key={item.key}
                  name={item.key}
                  component={item.component}
                />
              ))}
              <Stack.Screen 
                name="AddRoom" 
                component={AddRoomScreen}
                options={{
                  presentation: 'modal'
                }}
              />
              <Stack.Screen 
                name="EditRoom" 
                component={EditRoomScreen}
                options={{
                  presentation: 'modal'
                }}
              />
              <Stack.Screen 
                name="AddItem" 
                component={AddItemScreen}
                options={{
                  presentation: 'modal'
                }}
              />
              <Stack.Screen 
                name="EditItem" 
                component={EditItemScreen}
                options={{
                  presentation: 'modal'
                }}
              />
              <Stack.Screen 
                name="ViewItem" 
                component={ViewItemScreen}
                options={{
                  presentation: 'card'
                }}
              />
            </Stack.Navigator>
          </View>
          {!isDesktop && renderTabBar()}
        </View>
        {/* Only render the fixed nav bar for mobile web */}
        {Platform.OS === 'web' && !isDesktop && renderTabBar()}
      </div>
    );
  }
  // Native platforms
  return (
    <View style={[styles.root, !isDesktop && styles.mobileRoot, { backgroundColor: theme.background }]}> 
      {isDesktop && renderTabBar()}
      <View style={[
        styles.content, 
        { backgroundColor: theme.background },
        Platform.OS === 'web' && !isDesktop && { paddingBottom: 80 }
      ]}> 
        <HeaderBar titleKey={currentRouteName as keyof RootStackParamList} />
        <Stack.Navigator 
          screenOptions={{ 
            headerShown: false,
            presentation: 'card',
            headerStyle: { backgroundColor: theme.card, borderBottomColor: theme.border },
            headerTintColor: theme.text,
            // Avoid deprecated shadow* props on web by disabling card shadows
            ...(Platform.OS === 'web' ? { cardShadowEnabled: false } : {}),
          }}
          initialRouteName={currentRouteName as keyof RootStackParamList}
        >
          {NAV_ITEMS.map((item) => (
            <Stack.Screen
              key={item.key}
              name={item.key}
              component={item.component}
            />
          ))}
          <Stack.Screen 
            name="AddRoom" 
            component={AddRoomScreen}
            options={{
              presentation: 'modal'
            }}
          />
          <Stack.Screen 
            name="EditRoom" 
            component={EditRoomScreen}
            options={{
              presentation: 'modal'
            }}
          />
          <Stack.Screen 
            name="AddItem" 
            component={AddItemScreen}
            options={{
              presentation: 'modal'
            }}
          />
          <Stack.Screen 
            name="EditItem" 
            component={EditItemScreen}
            options={{
              presentation: 'modal'
            }}
          />
          <Stack.Screen 
            name="ViewItem" 
            component={ViewItemScreen}
            options={{
              presentation: 'card'
            }}
          />
        </Stack.Navigator>
      </View>
      {!isDesktop && renderTabBar()}
    </View>
  );
}

export function UnifiedNavigator() {
  const { user, loading: authLoading } = useAuth();
  const { tier } = useSubscription();
  const { width } = useWindowDimensions();
  const { setIsDesktop, setActiveTab } = useNavigationContext();
  const { theme } = useTheme();
  const navigationRef = useNavigationContainerRef();
  const [isNavigationReady, setIsNavigationReady] = useState(false);
  const [pendingDeepLink, setPendingDeepLink] = useState<{ type: 'login' | 'signup' } | null>(null);

  // Properly detect mobile web
  const isMobileWeb = Platform.OS === 'web' && width < 768;

  useEffect(() => {
    setIsDesktop(width >= 768);
  }, [width, setIsDesktop]);

  // Sync activeTab with current route
  useEffect(() => {
    const unsubscribe = navigationRef.addListener('state', () => {
      const currentRoute = navigationRef.getCurrentRoute();
      if (currentRoute && ['HomeTab', 'RoomsTab', 'ItemsTab', 'SettingsTab'].includes(currentRoute.name)) {
        setActiveTab(currentRoute.name as keyof RootStackParamList);
      }
    });
    return unsubscribe;
  }, [navigationRef, setActiveTab]);

  // Handle deep links with proper initialization
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined' && Platform.OS === 'web') {
      const hash = window.location.hash;
      const search = window.location.search;
      
      // Store deep link info if navigation isn't ready
      if (!isNavigationReady) {
        if (hash === '#signup' || search.includes('view=signup')) {
          setPendingDeepLink({ type: 'signup' });
        } else if (hash === '#login' || search.includes('view=login')) {
          setPendingDeepLink({ type: 'login' });
        }
        return;
      }

      // Process deep link if navigation is ready
      if (!user && navigationRef.isReady()) {
        if ((hash === '#signup' || search.includes('view=signup')) && pendingDeepLink?.type === 'signup') {
          navigationRef.navigate('SignUp' as never);
          setPendingDeepLink(null);
        } else if ((hash === '#login' || search.includes('view=login')) && pendingDeepLink?.type === 'login') {
          navigationRef.navigate('Login' as never);
          setPendingDeepLink(null);
        }
      }
    }
  }, [user, navigationRef, isNavigationReady, pendingDeepLink]);

  // Determine initial route with platform consideration
  const initialAuthRoute: 'Login' | 'SignUp' = (() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined' && Platform.OS === 'web') {
      const hash = window.location.hash;
      const search = window.location.search;
      if (hash === '#signup' || search.includes('view=signup')) {
        return 'SignUp';
      }
    }
    return 'Login';
  })();

  if (authLoading) {
    // Don't render any navigation stacks while determining the existing session – avoids the login flash.
    return (
      <SafeAreaProvider>
        <View style={{ flex: 1, backgroundColor: theme.card }} />
      </SafeAreaProvider>
    );
  }

  return (
    <SafeAreaProvider>
      <NavigationContainer
        ref={navigationRef}
        onReady={() => setIsNavigationReady(true)}
        documentTitle={{
          formatter: (options, route) => {
            let section = '';
            if (!route || route.name === 'HomeTab') {
              return 'Log My Home';
            } else if (TITLES[route.name]) {
              section = TITLES[route.name];
            } else if (route.name === 'AddRoom') {
              section = 'Add Room';
            } else if (route.name === 'EditRoom') {
              section = 'Edit Room';
            } else if (route.name === 'AddItem') {
              section = 'Add Item';
            } else if (route.name === 'EditItem') {
              section = 'Edit Item';
            } else if (route.name === 'ViewItem') {
              section = 'View Item';
            }
            return section ? `Log My Home - ${section}` : 'Log My Home';
          }
        }}
      >
        <Stack.Navigator 
          screenOptions={{ 
            headerShown: false,
            // Add platform-specific screen options
            ...(Platform.OS === 'web' ? { cardShadowEnabled: false } : {}),
            ...(isMobileWeb && {
              animation: 'slide_from_right',
              gestureEnabled: true,
              gestureDirection: 'horizontal',
            })
          }}
          {...(!user ? { initialRouteName: initialAuthRoute } : {})}
        >
          {!user ? (
            <>
              <Stack.Screen 
                name="Login" 
                component={LoginScreen}
                options={{
                  // Add platform-specific options for mobile web
                  ...(isMobileWeb && {
                    animation: 'slide_from_right',
                    gestureEnabled: true,
                  })
                }}
              />
              <Stack.Screen 
                name="SignUp" 
                component={SignUpScreen}
                options={{
                  // Add platform-specific options for mobile web
                  ...(isMobileWeb && {
                    animation: 'slide_from_right',
                    gestureEnabled: true,
                  })
                }}
              />
            </>
          ) : (
            <Stack.Screen name="Main" component={MainContent} />
          )}
        </Stack.Navigator>
      </NavigationContainer>
    </SafeAreaProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#181818',
  },
  mobileRoot: {
    flexDirection: 'column', // Change to column layout for mobile
  },
  sidebar: {
    backgroundColor: '#232323',
    paddingTop: 16,
    paddingBottom: 16,
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#333',
    minHeight: '100%',
    gap: 8,
  },
  toggleButton: {
    marginBottom: 24,
    alignSelf: 'flex-end',
    marginRight: 8,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    width: '100%',
    gap: 12,
  },
  navLabel: {
    color: '#fff',
    fontSize: 16,
  },
  activeNavItem: {
    backgroundColor: '#007AFF20',
  },
  activeNavLabel: {
    color: '#007AFF',
  },
  content: {
    flex: 1,
    backgroundColor: '#181818',
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#232323',
    borderTopWidth: 1,
    borderTopColor: '#333',
    paddingBottom: Platform.OS === 'ios' ? 24 : 12,
    paddingTop: 12,
    width: '100%', // Ensure full width
    ...(Platform.OS === 'web' ? {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
    } : {}),
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    gap: 4,
  },
  activeTabItem: {
    backgroundColor: '#007AFF10',
  },
  tabLabel: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '500',
    letterSpacing: 0.2,
    marginTop: 2,
  },
  activeTabLabel: {
    color: '#007AFF',
    fontWeight: '600',
  },
}); 